/* eslint-disable no-nested-ternary */
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import BadgeJobStatus from "Components/Generic/BadgeJobStatus";
import ActionButtons from "Components/Generic/ActionButtons";
import History, { Item } from "Components/Generic/History";
import Instructions from "Components/Generic/Instructions";
import PackingMaterials from "Components/Generic/PackingMaterials";
import { NotificationToast } from "Components/Generic/Toast/NotificationToast";
import UITabMaterial, { PanelProps } from "Components/Generic/UITabMaterial";
import MaintenanceTitlePage from "Components/MaintenanceTitlePage";
import { MovingResourcesProvider, useMovingResources } from "Context/MovingResources";
import { OrderProvider } from "Context/OrderContext";
import { useAppState } from "Context/StateContext";
import { format } from "date-fns";
import useActionAutomaticSequence from "Hooks/useActionAutomaticSequence";
import { useActionsJob } from "Hooks/useActionJob";
import { useActionOrder } from "Hooks/useActionOrder";
import { useOrderContext } from "Hooks/useOrderContext";
import { useQueryURL } from "Hooks/useQueryURL";
import { AutomaticSequencing } from "Models/automatic.sequencing";
import { Instruction } from "Models/instruction";
import { Order } from "Models/order";
import { CustomerLocations, RoomJob, TaskDate } from "Models/sales";
import { Task } from "Models/task";
import { User } from "Models/user";
import { useActionFolders } from "Hooks/useActionFolders";
import { StorageBox } from "Models/storage.box";
import { useTranslation } from "react-i18next";
import { Prompt, useHistory, useParams } from "react-router-dom";
import OrderService from "Services/order.service";
import { getOrderCustomerDisplayName } from "Util/names";
import * as yup from "yup";

import { MOVING_AGENT_COMPANY_PROFILE_ID, PRIVATE_ROUTES } from "../../../../../constants";

import { File } from "../../../../../models/file";
import { Folder } from "../../../../../models/folder";

import {
  AutomaticSequenceOptions,
  CompanyProfile,
  ItemJobSubStatus,
  JobStatus,
  ResourceName,
  ShipmentType,
  SubStatusUsedBy,
} from "../../../../../util/enums";
import { getOrderFolder } from "../../../../../util/getFolders";
import AccessComponent from "./Access";
import Details from "./Details";
import OrderInventory from "./Inventory";
import LoadingSkeleton from "./skeleton";
import useStyles from "./styles";
import { JobDocumentsTab } from "./Tabs";
import Tasks from "./Tasks";
import WaybillTab from "./Waybill";
import { checkResourceCan, FN_NAMES, hasPermission, isShowAction } from "Util/permissions";
import { ResourceCanEnum } from "Util/enums/accessRule";
import Files from "Components/Generic/Files";
import { CompanyConfigsService } from "Services/company-configs.service";
import WaybillsService from "Services/waybill.service";
import { Waybill } from "Models/waybill";
import { validateWaybillForDraft } from "../../Waybills/Maintenance/validation/validateWaybillForDraft";
import { validateWaybillForPublish } from "../../Waybills/Maintenance/validation/validateWaybillForPublish";

type StagedWaybill = Waybill & { __localDraft?: boolean };

export type StagedShipmentAction = {
  shipmentId: number;
  companyId: number;
  action: "cancel" | "revert_to_draft";
};

function buildStagedCandidate(w: StagedWaybill, orderId: number): Waybill {
  return {
    ...w,
    id: undefined,
    // eslint-disable-next-line no-underscore-dangle
    __localDraft: undefined,
    jobs: [{ id: orderId } as any],
  } as Waybill;
}

type OrderMaintenanceScreenProps = {
  showTitle?: boolean;
  id?: number;
  className?: string;
  onCancel?: () => void;
  onSave?: () => void;
  quickOrderMode?: boolean;
};

interface SearchParams {
  tab?: string;
}

const OrderMaintenanceScreen = (props: OrderMaintenanceScreenProps) => {
  const { showTitle, id: taskIdProps, className, onCancel, onSave, quickOrderMode } = props;
  const queries = useQueryURL<SearchParams>();
  const { tab } = queries("tab");

  const {
    order,
    setOrder,
    hasTaskToPublish,
    handleUpdateOrder,
    setChangedOptions,
    generalErrors,
    folders,
    setFolders,
  } = useOrderContext();

  const history = useHistory();
  const {
    currentUser,
    userPermissions,
    agentPermissions,
    setAgentPermissions,
    setAgentCompany,
    agentCompany,
  } = useAppState();

  const { id: taskIdParam } = useParams<{ id?: string | undefined }>();

  const { t: translate } = useTranslation();

  const taskId = taskIdParam || taskIdProps;

  const classes = useStyles();

  const {
    sendOrder,
    sendOrderAndPublish,
    isUpdatingOrder,
    setHasUnsavedChanges,
    hasUnsavedChanges,
    getAllUsers,
  } = useActionOrder();

  const { loadingDetailsResources, ...externalList } = useMovingResources();

  const [sequence, setSequence] = useState<AutomaticSequencing>();
  const [currentTab, setCurrentTab] = useState<number>(0);
  const [draftWaybills, setDraftWaybills] = useState<Waybill[]>([]);
  const [stagedShipmentActions, setStagedShipmentActions] = useState<StagedShipmentAction[]>([]);

  const [typeFolder, setTypeFolder] = useState<"Orders" | "Quote">("Orders");

  const [errors, setErrors] = useState<yup.ValidationError[]>([]);

  const [hasError, setHasError] = useState<boolean>(false);

  const [isValid, setIsValid] = useState<boolean>(false);

  const [customerOriginErrors, setCustomerOriginErrors] = useState<yup.ValidationError[]>([]);
  const [customerDestinationErrors, setCustomerDestinationErrors] = useState<yup.ValidationError[]>(
    []
  );

  const { getAutomaticSequenceByType } = useActionAutomaticSequence();

  const { getInventoryList, getJobHistories } = useActionsJob({
    jobId: taskId,
  });

  const { data: inventoryData, isFetching: isLoadingInventory } = getInventoryList([
    ItemJobSubStatus.QUOTED,
  ]);
  const { data: actualInventoryData, isFetching: isLoadingActualInventory } = getInventoryList([
    ItemJobSubStatus.ACTUAL,
  ]);

  const { data: historyData } = getJobHistories();

  const { getOrderFolders, getQuoteFolders, mutateFolders } = useActionFolders({ jobId: taskId });

  const { data: orderFolderData, isFetching: isOrderFolderFetching } = getOrderFolders();
  const { data: quoteFolderData, isFetching: isQuoteFolderFetching } = getQuoteFolders();

  const [foldersDeleted, setFoldersDeleted] = useState<Folder[]>([]);
  const [filesDeleted, setFilesDeleted] = useState<Folder[]>([]);
  const [orderPermission, setOrderPermission] = useState<{
    edit: boolean;
    create: boolean;
  }>({
    edit: false,
    create: false,
  });

  getAutomaticSequenceByType(AutomaticSequenceOptions.OPERATIONS, !order?.id, setSequence);
  const { data: usersData, isFetching } = getAllUsers();

  useEffect(() => {
    if (userPermissions && userPermissions?.length > 0) {
      setOrderPermission({
        edit:
          checkResourceCan(userPermissions, ResourceName.ORDERS, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_OPERATIONS_ORDERS_EDIT),
        create:
          checkResourceCan(userPermissions, ResourceName.ORDERS, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_OPERATIONS_ORDERS_ADD_NEW),
      });
    }
  }, [userPermissions]);

  useEffect(() => {
    if (isValid) handleValidations();
  }, [order]);

  useEffect(() => {
    if (quickOrderMode && !taskId) {
      setOrder((prev) => ({ ...(prev || ({} as Order)), quickOrder: true } as Order));
    }
  }, [quickOrderMode, taskId, setOrder]);

  useEffect(() => {
    if (inventoryData || actualInventoryData) {
      setOrder((prev) => ({
        ...getOrderFolder(translate, {
          ...prev,
          inventory: inventoryData?.quotedInventory || [],
          actualInventory: actualInventoryData?.actualInventory || [],
        }),
      }));
    }
  }, [inventoryData, actualInventoryData]);

  useEffect(() => {
    if (historyData && historyData?.length) {
      setOrder((prev) => ({ ...prev, histories: historyData }));
    }
  }, [historyData]);

  useEffect(() => {
    if (orderFolderData) {
      setFolders(typeFolder === ResourceName.QUOTE ? quoteFolderData ?? [] : orderFolderData ?? []);
    }
  }, [orderFolderData, quoteFolderData, typeFolder]);

  const handleChangeOptions = (newOption: string, noChanges?: boolean) => {
    if (!noChanges) setHasUnsavedChanges(true);
    setChangedOptions((options) => [
      ...options.filter((option) => option.toLowerCase() !== newOption.toLowerCase()),
      newOption,
    ]);
  };

  useQuery(
    ["company.configs"],
    async () => {
      if (taskId) {
        const response = await CompanyConfigsService.getByCreatedCompany([Number(taskId)]);
        const permission = response?.data;

        setAgentCompany(permission?.[Number(taskId)]?.companyId);

        setAgentPermissions(permission);

        return permission || null;
      }

      return null;
    },
    {
      enabled: !!taskId,
    }
  );

  const { isFetching: loadingOrder } = useQuery(
    ["orders.get"],
    () =>
      OrderService.getById(Number(taskId), {
        isSalesApp: true,
        isNewVersion: true,
        companyId: currentUser?.company.profileId === 6 ? agentCompany : currentUser?.companyId,
        loggedCompanyId: currentUser?.company?.profileId === 6 ? currentUser?.companyId : undefined,
      }),
    {
      enabled: !!taskId,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) {
          const payload = response.data;

          const isMilitaryCompany = Boolean(
            payload.company?.isMilitary ?? currentUser?.company?.isMilitary
          );

          const militaryReweighAndRelatedFlat = isMilitaryCompany
            ? {
                grossWeight:
                  payload.reweighGrossWeight !== undefined && payload.reweighGrossWeight !== null
                    ? payload.reweighGrossWeight
                    : "",
                tareWeight:
                  payload.reweighTareWeight !== undefined && payload.reweighTareWeight !== null
                    ? payload.reweighTareWeight
                    : "",
                netWeight:
                  payload.reweighNetWeight !== undefined && payload.reweighNetWeight !== null
                    ? payload.reweighNetWeight
                    : "",
                grade: payload.grade ?? "",
                sitInDate: payload.sitInDate ?? "",
                sitOutDate: payload.sitOutDate ?? "",
                sitNumber: payload.sitNumber ?? "",
                scacCode: payload.scacCode ?? "",
                scacDescription: payload.scacDescription ?? "",
                scacPhone: payload.scacPhone ?? "",
                scacEmail: payload.scacEmail ?? "",
                scacClaimsPhone: payload.scacClaimsPhone ?? "",
                scacClaimsEmail: payload.scacClaimsEmail ?? "",
              }
            : {};

          const militaryFlat = { ...militaryReweighAndRelatedFlat };

          setOrder({
            ...order,
            ...response.data,
            ...militaryFlat,
            storageBoxes: [
              ...(response.data.storageBoxes?.filter((sb: StorageBox) => sb.valid && sb.isActive) ||
                []),
            ],
          });
        }
      },
    }
  );
  const getItemsToHistory = () => {
    if (!taskId) {
      const firstHistoryIp = (
        order?.histories?.find((history) => history?.userIpAddress)?.userIpAddress ?? ""
      ).trim();
      return [
        {
          title: "Order created",
          activityDate: `${format(new Date(), "dd/MM/yyyy HH:mmbb")}`,
          activityUser: `${currentUser?.name} ${currentUser?.lastName}`,
          activityUserIp: firstHistoryIp,
        } as Item,
      ];
    }

    if (order?.histories && order?.histories?.length > 0) {
      return order?.histories?.map((history) => {
        const user = history?.user ? `${history?.user?.name} ${history?.user?.lastName}` : "";

        return {
          title: history?.action,
          activityDate: history?.updatedAt
            ? `${format(new Date(history?.updatedAt), "dd/MM/yyyy HH:mmbb")}`
            : "",
          activityUser: user,
          activityUserIp: history.userIpAddress ?? "",
        } as Item;
      });
    }

    return undefined;
  };

  const goBack = () => {
    if (history.action !== "POP") {
      history.goBack();
    } else {
      history.replace("/");
    }
  };

  const onClickCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      goBack();
    }
  };

  const { mutateAsync } = mutateFolders(
    Number(taskId),
    folders || [],
    Number(currentUser?.companyId),
    foldersDeleted,
    filesDeleted
  );

  const showWaybillValidationError = useCallback(
    (errors: yup.ValidationError[], reason?: "no_jobs_or_customers") => {
      if (reason === "no_jobs_or_customers") {
        NotificationToast.error(translate("youShouldAddAtLeastOneOrderToTheShipment"), {
          id: "add-order-to-shipment",
        });
        return;
      }
      NotificationToast.error(errors?.[0]?.message || translate("anErrorOccurredTryAgain"));
    },
    [translate]
  );

  const flushStagedShipmentActions = useCallback(async (): Promise<boolean> => {
    if (!stagedShipmentActions.length) return true;
    for (const staged of stagedShipmentActions) {
      try {
        const newStatus = staged.action === "cancel" ? "Cancelled" : "Draft";
        // eslint-disable-next-line no-await-in-loop
        await WaybillsService.update(staged.shipmentId, {
          statusShipment: newStatus,
          isDraft: true,
          companyId: staged.companyId,
          shipmentTrackingId: null,
        });
      } catch (e) {
        console.error("Failed to update shipment", staged.shipmentId, ":", e);
        NotificationToast.error(translate("anErrorOccurredTryAgain"));
        return false;
      }
    }
    setStagedShipmentActions([]);
    return true;
  }, [stagedShipmentActions, translate]);

  const publishStagedWaybillsForOrder = useCallback(
    async (orderId: number): Promise<boolean> => {
      if (!draftWaybills.length || !currentUser?.companyId) return true;
      let anySavedAsDraft = false;
      for (const w of draftWaybills) {
        const candidate = buildStagedCandidate(w, orderId);
        const isAir = candidate.shipmentType?.toLowerCase() === ShipmentType.AIR.toLowerCase();
        const basePayload = isAir ? { ...candidate, containers: [] } : candidate;

        const publishResult = validateWaybillForPublish(candidate, translate, {
          companyProfileInitials: currentUser?.company?.profile?.initials,
        });

        let isDraft = false;
        if (!publishResult.ok) {
          if (publishResult.reason === "no_jobs_or_customers") {
            showWaybillValidationError(publishResult.errors, publishResult.reason);
            return false;
          }
          const draftResult = validateWaybillForDraft(candidate, translate, {
            companyProfileInitials: currentUser?.company?.profile?.initials,
          });
          if (!draftResult.ok) {
            showWaybillValidationError(draftResult.errors);
            return false;
          }
          isDraft = true;
        }

        try {
          // eslint-disable-next-line no-await-in-loop
          await WaybillsService.create({
            ...basePayload,
            companyId: currentUser.companyId,
            isDraft,
            ...(quickOrderMode ? { quickOrder: true } : {}),
          });
          if (isDraft) anySavedAsDraft = true;
        } catch (e) {
          console.error("WaybillsService.create failed:", e);
          NotificationToast.error(translate("anErrorOccurredTryAgain"));
          return false;
        }
      }
      if (anySavedAsDraft) {
        NotificationToast.success(translate("shipmentSavedAsDraft"), {
          id: "order-flow-shipment-saved-as-draft",
        });
      }
      return true;
    },
    [
      draftWaybills,
      currentUser?.companyId,
      currentUser?.company?.profile?.initials,
      quickOrderMode,
      translate,
      showWaybillValidationError,
    ]
  );

  const handleSaveAndPublishOrder = () => {
    if (!isValid) handleValidate();
    setIsValid(true);
    if (errors.length === 0) {
      sendOrderAndPublish((id) => {
        if (onSave) onSave();

        void (async () => {
          const effectiveId = id ?? Number(taskId);
          if (effectiveId && draftWaybills.length) {
            const ok = await publishStagedWaybillsForOrder(effectiveId);
            if (!ok) return;
          }
          const flushed = await flushStagedShipmentActions();
          if (!flushed) return;
          history.replace(PRIVATE_ROUTES.ORDERS);
          if (currentUser?.companyId) mutateAsync(effectiveId);
        })();
      }, sequence);
    }
  };

  const schema = yup.object().shape({
    jobTypeId: yup.number().required(translate("thisFieldIsRequired")),
    reference: yup.string().required(translate("thisFieldIsRequired")),
    status: yup.string().required(translate("thisFieldIsRequired")),
  });

  const customerSchema = yup.object().shape({
    fullName: yup.string().required(translate("thisFieldIsRequired")),
    emailCustomer: yup.string().required(translate("thisFieldIsRequired")),
    address: yup.string().required(translate("thisFieldIsRequired")),
  });

  const handleValidateCustomer = (
    type: "origin" | "destination",
    setErrors: (value: yup.ValidationError[]) => void,
    customerLocation?: CustomerLocations
  ) => {
    const customer = customerLocation?.customer;
    const location = customerLocation?.locations?.[0];

    try {
      customerSchema.validateSync(
        {
          fullName: customer?.fullName,
          emailCustomer: customer?.email,
          address: location?.longAddress,
        },
        { abortEarly: false }
      );
      setErrors([]);

      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) setErrors(error?.inner);

      return false;
    }
  };

  const handleValidate = () => {
    try {
      schema.validateSync(
        {
          jobTypeId: order?.jobTypeId,
          reference: order?.reference,
          status: order?.jobStatus,
        },
        { abortEarly: false }
      );
      setErrors([]);

      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) setErrors(error?.inner);

      return false;
    }
  };

  const handleValidations = () => {
    const orderIsValid = handleValidate();

    const destinationCustomerIsValid = handleValidateCustomer(
      "destination",
      setCustomerDestinationErrors,
      order?.destination
    );

    const originCustomerIsValid = handleValidateCustomer(
      "origin",
      setCustomerOriginErrors,
      order?.origin
    );

    const isValid = [
      orderIsValid,
      destinationCustomerIsValid,
      originCustomerIsValid,
      // originContactsIsValid,
      // destinationContactsIsValid,
    ].every((valid) => valid === true);

    setHasError(!isValid);

    return isValid;
  };

  const handleSaveOrder = useCallback(() => {
    let validatedSuccessfully: boolean = true;

    if (!isValid) {
      validatedSuccessfully = handleValidations();
    }

    setIsValid(true);

    const runSendOrder = () => {
      sendOrder((orderId) => {
        if (onSave) onSave();
        void (async () => {
          const effectiveOrderId = orderId ?? Number(taskId);
          if (effectiveOrderId && draftWaybills.length) {
            const ok = await publishStagedWaybillsForOrder(effectiveOrderId);
            if (!ok) return;
          }
          const flushed = await flushStagedShipmentActions();
          if (!flushed) return;
          if (currentUser?.companyId) mutateAsync(effectiveOrderId);
          history.push(PRIVATE_ROUTES.ORDERS);
        })();
      }, sequence);
    };

    if (currentTab === 0) {
      if (!hasError && validatedSuccessfully) {
        runSendOrder();
      }
    } else {
      runSendOrder();
    }
  }, [
    isValid,
    handleValidations,
    setIsValid,
    currentTab,
    hasError,
    sendOrder,
    onSave,
    draftWaybills,
    publishStagedWaybillsForOrder,
    flushStagedShipmentActions,
    currentUser?.companyId,
    mutateAsync,
    taskId,
    sequence,
    history,
  ]);

  const checkDetailsOrderForErrors = useMemo((): boolean => {
    const conditionsToIncludeError = ["type", "mode"];

    const hasError = generalErrors?.find((error) => {
      return conditionsToIncludeError.some((conditionError) =>
        error?.path?.includes(conditionError)
      );
    });

    return !!hasError;
  }, [generalErrors]);

  const onSwitchFolder = () => {
    setTypeFolder((prev) => (prev === "Orders" ? "Quote" : "Orders"));
  };

  const checkAccessOrderForErrors = useMemo((): boolean => {
    const conditionsToIncludeError = ["pickupAccess", "deliveryAccess"];

    const hasError = generalErrors?.find((error) => {
      return conditionsToIncludeError.some((conditionError) =>
        error?.path?.includes(conditionError)
      );
    });

    return !!hasError;
  }, [generalErrors]);

  const activityPanel: PanelProps = {
    menuItem: translate("activity"),
    key: "activity-panel",
    render: () => <History items={getItemsToHistory()} isModal />,
  };

  const detailsPanel: PanelProps = {
    menuItem: translate("detail"),
    key: "details-panel",
    hasError:
      checkDetailsOrderForErrors ||
      !!customerDestinationErrors.length ||
      !!customerOriginErrors.length,
    className: classes.uiTab,
    loading: loadingOrder || loadingDetailsResources,
    skeleton: () => <LoadingSkeleton />,
    render: () => (
      <Details
        customerOriginErrors={customerOriginErrors}
        customerDestinationErrors={customerDestinationErrors}
        errors={errors}
        onSavePublish={hasTaskToPublish ? () => handleSaveAndPublishOrder() : undefined}
        onChange={(noChanges) => handleChangeOptions(translate("detail"), noChanges)}
        onSave={handleSaveOrder}
        onCancel={onClickCancel}
        isUpdating={isUpdatingOrder}
        sequence={sequence}
        isEdit={!!taskId}
        externalList={externalList}
        allUsers={usersData as User[]}
        usersLoading={isFetching}
        showActionButtons={!quickOrderMode}
        showGeneralComments={!quickOrderMode}
        compact={quickOrderMode}
      />
    ),
  };

  const inventoryPanel: PanelProps = {
    menuItem: translate("inventory"),
    key: "inventory-panel",
    className: classes.uiTab,
    loading: loadingOrder || isLoadingInventory || isLoadingActualInventory,
    skeleton: () => <LoadingSkeleton />,
    render: () => (
      <OrderInventory
        order={order}
        destinations={order?.destination?.locations || []}
        inventory={order?.inventory || []}
        actualInventory={order?.actualInventory || []}
        defaultMode={order?.mode || ""}
        quoteJobId={order?.jobId || -1}
        orderStatus={order?.jobStatus}
        isUpdating={isUpdatingOrder}
        update={(attribute, roomJob) => {
          handleUpdateOrder(attribute, roomJob);
          handleChangeOptions(translate("inventory"));
        }}
        updateDestinationAddress={(value) =>
          handleUpdateOrder && handleUpdateOrder("destination", { customer: {}, locations: value })
        }
        addStorageBox={() => {
          handleChangeOptions(translate("storageBoxAdded"));
        }}
        removeStorageBox={() => {
          handleChangeOptions(translate("storageBoxRemoved"));
        }}
        onSavePublish={hasTaskToPublish ? () => handleSaveAndPublishOrder() : undefined}
        onSave={handleSaveOrder}
        onCancel={onClickCancel}
        showActionButtons={!quickOrderMode}
        compact={quickOrderMode}
        quotedViewOnly={!!order?.id}
        externalList={externalList}
        storageBoxes={order?.storageBoxes}
      />
    ),
  };

  const packingMaterialPanel: PanelProps = {
    menuItem: translate("material"),
    key: "packingMaterial-panel",
    className: classes.uiTab,
    loading: loadingOrder || isLoadingInventory || isLoadingActualInventory,
    skeleton: () => <LoadingSkeleton />,
    render: () => (
      <PackingMaterials
        destinations={order?.destination?.locations || []}
        inventory={order?.inventory}
        jobId={order?.jobId}
        handleUpdate={(roomJob: RoomJob[]) => {
          if (handleUpdateOrder) {
            handleUpdateOrder("inventory", roomJob);
            handleChangeOptions(translate("material"));
          }
        }}
        onCancel={onClickCancel}
        onSavePublish={hasTaskToPublish ? () => handleSaveAndPublishOrder() : undefined}
        onSave={handleSaveOrder}
        isUpdating={isUpdatingOrder}
        externalList={externalList}
        isOnViewMode={
          !(
            currentUser?.company.profileId !== MOVING_AGENT_COMPANY_PROFILE_ID ||
            !agentPermissions?.[Number(taskId)]?.order?.sections?.material?.enabled
          ) &&
          agentPermissions?.[Number(taskId)]?.order?.sections?.material?.fields?.general?.view ===
            true
        }
      />
    ),
  };

  const tasksPanel: PanelProps = {
    menuItem: translate("task"),
    key: "tasks-panel",
    className: classes.uiTab,
    loading: loadingOrder,
    skeleton: () => <LoadingSkeleton />,
    render: () => (
      <Tasks
        order={order}
        tasks={order?.tasks}
        taskDates={order?.taskDates}
        onCancel={onClickCancel}
        handleUpdate={(tasks?: Task[], taskDates?: TaskDate[]) => {
          if (tasks) handleUpdateOrder("tasks", tasks);
          if (taskDates) handleUpdateOrder("taskDates", taskDates);
        }}
        onSavePublish={hasTaskToPublish ? handleSaveAndPublishOrder : undefined}
        onSave={handleSaveOrder}
        isUpdating={isUpdatingOrder}
        externalList={externalList}
      />
    ),
  };

  const accessPanel: PanelProps = {
    menuItem: translate("access"),
    key: "access-panel",
    className: classes.uiTab,
    hasError: checkAccessOrderForErrors,
    loading: loadingOrder,
    skeleton: () => <LoadingSkeleton />,
    render: () => (
      <AccessComponent
        data={order}
        addresses={{
          origin: order?.origin?.locations || [],
          destination: order?.destination?.locations || [],
        }}
        handleUpdate={(attribute: string, value: any) => {
          handleUpdateOrder(attribute, value);

          if (!order?.id) {
            const valueKeys = Object.keys(value);

            if (!(valueKeys.length === 1 && valueKeys[0] === "instructions"))
              handleChangeOptions(translate("access"));
          }
        }}
        onCancel={onClickCancel}
        onSavePublish={hasTaskToPublish ? () => handleSaveAndPublishOrder() : undefined}
        onSave={handleSaveOrder}
        isUpdating={isUpdatingOrder}
        errors={generalErrors}
        viewMode={!!order?.id}
        externalList={externalList}
      />
    ),
  };

  const instructionsPanel: PanelProps = {
    menuItem: translate("instruction"),
    className: classes.uiTab,
    loading: loadingOrder,
    skeleton: () => <LoadingSkeleton />,
    key: "instructions-panel",
    render: () => (
      <Instructions
        instructions={order?.instructions || []}
        handleUpdate={(instructions: Instruction[]) => {
          handleUpdateOrder("instructions", instructions);
          handleChangeOptions(translate("instruction"));
        }}
        onSavePublish={hasTaskToPublish ? () => handleSaveAndPublishOrder() : undefined}
        onCancel={onClickCancel}
        onSave={handleSaveOrder}
        isUpdating={isUpdatingOrder}
        externalList={externalList}
        isOrder={order?.jobStatus !== JobStatus.QUOTE}
        isOnViewMode={
          !(
            currentUser?.company.profileId !== MOVING_AGENT_COMPANY_PROFILE_ID ||
            agentPermissions?.[Number(taskId)]?.order?.sections?.instruction?.fields?.general
              ?.addNew === true
          ) &&
          agentPermissions?.[Number(taskId)]?.order?.sections?.instruction?.fields?.general
            ?.view === true
        }
      />
    ),
  };

  // eslint-disable-next-line unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars
  const shipmentPanel: PanelProps = {
    menuItem: translate("shipments"),
    className: classes.uiTab,
    loading: loadingOrder,
    skeleton: () => <LoadingSkeleton />,
    key: "shipment-panel",
    render: () => (
      <WaybillTab
        order={order}
        waybills={[...(draftWaybills ?? []), ...(order?.shipments ?? [])]}
        isUpdating={isUpdatingOrder}
        onSave={handleSaveOrder}
        onCancel={onClickCancel}
        onSaveAndPublish={hasTaskToPublish ? () => handleSaveAndPublishOrder() : undefined}
        inlineDraftCreation={quickOrderMode}
        showActionButtons={!quickOrderMode}
        compact={quickOrderMode}
        onUpdate={(waybills) => {
          const drafts = (waybills ?? []).filter((w: StagedWaybill) => !!w?.__localDraft);
          const persisted = (waybills ?? []).filter((w: StagedWaybill) => !w?.__localDraft);
          setDraftWaybills(drafts);
          handleUpdateOrder("shipments", persisted);
        }}
        onRemoveWithAction={(shipmentId: number, companyId: number | undefined, action: "cancel" | "revert_to_draft") => {
          const resolvedCompanyId = companyId ?? currentUser?.companyId ?? 0;
          setStagedShipmentActions((prev) => [...prev, { shipmentId, companyId: resolvedCompanyId, action }]);
        }}
      />
    ),
  };

  const filesPanel: PanelProps = {
    menuItem: translate("folder"),
    className: classes.uiTab,
    loading: loadingOrder,
    skeleton: () => <LoadingSkeleton />,
    key: "files-panel",
    render: () => (
      <Files
        onCancel={onClickCancel}
        onSavePublish={hasTaskToPublish ? () => handleSaveAndPublishOrder() : undefined}
        onSave={handleSaveOrder}
        folders={folders}
        foldersDeleted={foldersDeleted}
        filesDeleted={filesDeleted}
        inventory={typeFolder === ResourceName.QUOTE ? order?.inventory : undefined}
        handleUpdate={(folders?: Folder[], foldersDeleted?: Folder[], filesDeleted?: File[]) => {
          if (folders) setFolders(folders || []);
          if (foldersDeleted) setFoldersDeleted(foldersDeleted || []);
          if (filesDeleted) setFilesDeleted(filesDeleted || []);
          handleChangeOptions(translate("folder"));
        }}
        isUpdating={isUpdatingOrder}
        loading={loadingOrder || isOrderFolderFetching || isQuoteFolderFetching}
        typeFolder={typeFolder}
        nextTypeFolder={typeFolder === "Orders" ? "Quote" : "Orders"}
        onSwitchTypeFolder={onSwitchFolder}
        canAddPhoto={
          currentUser?.company.profileId !== MOVING_AGENT_COMPANY_PROFILE_ID ||
          agentPermissions?.[Number(taskId)]?.order?.sections?.folder?.fields?.photosVideos
            .addNew === true
        }
        canViewPhoto={
          currentUser?.company.profileId !== MOVING_AGENT_COMPANY_PROFILE_ID ||
          agentPermissions?.[Number(taskId)]?.order?.sections?.folder?.fields?.photosVideos.view ===
            true
        }
        canAddDocument={
          currentUser?.company.profileId !== MOVING_AGENT_COMPANY_PROFILE_ID ||
          agentPermissions?.[Number(taskId)]?.order?.sections?.folder?.fields?.documents.addNew ===
            true
        }
        canViewDocument={
          currentUser?.company.profileId !== MOVING_AGENT_COMPANY_PROFILE_ID ||
          agentPermissions?.[Number(taskId)]?.order?.sections?.folder?.fields?.documents.view ===
            true
        }
        showActionButtons={!quickOrderMode}
        hasAbsoluteActionButton={!quickOrderMode}
        compact={quickOrderMode}
      />
    ),
  };

  const leftPanels = taskId
    ? !isShowAction(currentUser, orderPermission.edit)
      ? []
      : [
          ...(currentUser?.company.profileId !== MOVING_AGENT_COMPANY_PROFILE_ID ||
          agentPermissions?.[Number(taskId)]?.order?.sections?.detail?.enabled
            ? [detailsPanel]
            : []),
          ...(currentUser?.company.profileId !== MOVING_AGENT_COMPANY_PROFILE_ID ||
          agentPermissions?.[Number(taskId)]?.order?.sections?.inventory?.fields?.actualInventory
            ?.view
            ? [inventoryPanel]
            : []),
          packingMaterialPanel,
          ...(currentUser?.company.profileId !== MOVING_AGENT_COMPANY_PROFILE_ID ||
          agentPermissions?.[Number(taskId)]?.order?.sections?.task?.fields?.general?.view
            ? [tasksPanel]
            : []),
          ...(currentUser?.company.profileId !== MOVING_AGENT_COMPANY_PROFILE_ID ||
          agentPermissions?.[Number(taskId)]?.order?.sections?.access?.fields?.general?.view
            ? [accessPanel]
            : []),
          ...(currentUser?.company.profileId !== MOVING_AGENT_COMPANY_PROFILE_ID ||
          agentPermissions?.[Number(taskId)]?.order?.sections?.instruction?.fields?.general?.view
            ? [instructionsPanel]
            : []),
          ...(currentUser?.company.profile.initials === CompanyProfile.MV
            ? [shipmentPanel]
            : []),
          filesPanel,
        ]
    : !isShowAction(currentUser, orderPermission.create)
    ? []
    : [
        ...(currentUser?.company.profileId !== MOVING_AGENT_COMPANY_PROFILE_ID ||
        agentPermissions?.[Number(taskId)]?.order?.sections?.detail?.enabled
          ? [detailsPanel]
          : []),
        ...(currentUser?.company.profileId !== MOVING_AGENT_COMPANY_PROFILE_ID ||
        agentPermissions?.[Number(taskId)]?.order?.sections?.inventory?.fields?.actualInventory
          ?.view
          ? [inventoryPanel]
          : []),
        packingMaterialPanel,
        ...(currentUser?.company.profileId !== MOVING_AGENT_COMPANY_PROFILE_ID ||
        agentPermissions?.[Number(taskId)]?.order?.sections?.task?.fields?.general?.view
          ? [tasksPanel]
          : []),
        ...(currentUser?.company.profileId !== MOVING_AGENT_COMPANY_PROFILE_ID ||
        agentPermissions?.[Number(taskId)]?.order?.sections?.access?.fields?.general?.view
          ? [accessPanel]
          : []),
        ...(currentUser?.company.profileId !== MOVING_AGENT_COMPANY_PROFILE_ID ||
        agentPermissions?.[Number(taskId)]?.order?.sections?.instruction?.fields?.general?.view
          ? [instructionsPanel]
          : []),
        ...(currentUser?.company.profile.initials === CompanyProfile.MV
          ? [shipmentPanel]
          : []),
        filesPanel,
      ];

  if (tab) {
    const panelsArray = leftPanels;
    const index = panelsArray.findIndex((panel) => panel.key === tab);

    if (index > -1 && currentTab !== index) {
      setCurrentTab(index);
    }
  }

  const activityPanels = [activityPanel];

  const quickOrderPanels: PanelProps[] = [
    detailsPanel,
    ...(currentUser?.company.profile.initials === CompanyProfile.MV ? [shipmentPanel] : []),
    filesPanel,
  ];

  const renderQuickOrderPanel = (panel: PanelProps, index: number) => (
    <section
      key={panel.key}
      className={classes.quickOrderPanel}
      data-cy={`quick-order-panel-${panel.key}`}
    >
      <div className={classes.quickOrderPanelHeader}>
        <span className={classes.quickOrderStep}>{index + 1}</span>
        <h3 className={classes.quickOrderPanelTitle}>{panel.menuItem}</h3>
      </div>
      <div className={classes.quickOrderPanelBody}>
        {panel.loading ? panel.skeleton?.() : panel.render?.()}
      </div>
    </section>
  );

  const titlePage = () => {
    if (showTitle !== false) {
      const customerFullname = getOrderCustomerDisplayName(order);

      const reference =
        order && order?.reference
          ? `${order?.reference || ""}${
              order.sequence && Number(order.sequence) > 0 ? `/${order.sequence}` : ""
            } `
          : "";

      return (
        <MaintenanceTitlePage
          titlePage={translate("order")}
          reference={reference}
          customerFullname={customerFullname}
          badge={
            <BadgeJobStatus
              value={order?.jobStatus}
              className={classes.titleStatusBadge}
              dataCy="status"
            />
          }
        />
      );
    }

    return null;
  };

  return (
    <div className={className || classes.mainDiv}>
      {titlePage()}
      <Prompt when={hasUnsavedChanges} message="" />
      {quickOrderMode ? (
        <div className={classes.quickOrderLayout} data-cy="quick-order-layout">
          {quickOrderPanels.map(renderQuickOrderPanel)}
          <div className={classes.quickOrderFooter} data-cy="quick-order-footer">
            <ActionButtons
              cancelDisabled={isUpdatingOrder}
              saveDisabled={isUpdatingOrder}
              loading={isUpdatingOrder}
              onCancel={onClickCancel}
              onSavePublish={hasTaskToPublish ? () => handleSaveAndPublishOrder() : undefined}
              onSave={handleSaveOrder}
              textSave={order?.id ? translate("update") : translate("create")}
            />
          </div>
        </div>
      ) : (
        <UITabMaterial
          showActivityButton
          activityPanels={activityPanels}
          leftPanels={leftPanels}
          leftDataCy="order-tabs"
          selectedLeftPanel={currentTab}
          onChangeLeftPanel={(_, value) => {
            setCurrentTab(Number(value));
          }}
        />
      )}
    </div>
  );
};

const OrderMaintenance = (props: OrderMaintenanceScreenProps) => {
  return (
    <OrderProvider>
      <MovingResourcesProvider type={SubStatusUsedBy.ORDERS}>
        <OrderMaintenanceScreen {...props} />
      </MovingResourcesProvider>
    </OrderProvider>
  );
};

export default OrderMaintenance;
